/**
 * A page in a pop-up over the current view (Notion's "peek"): database rows open
 * here, so the table stays where it was. "Openen als pagina" moves it to a tab.
 */
import { useTranslation } from 'react-i18next';
import { Dialog as D } from 'radix-ui';
import { Maximize2 } from 'lucide-react';
import { usePages } from '../db/context';
import { useUi } from '../state/ui';
import { CloseButton } from './CloseButton';
import { PageView } from './PageView';

export function PagePeek() {
  const { t } = useTranslation();
  const peekPageId = useUi((s) => s.peekPageId);
  const pages = usePages();
  const page = peekPageId ? pages?.byId.get(peekPageId) : undefined;
  const close = () => useUi.getState().set({ peekPageId: null });

  return (
    <D.Root open={!!page} onOpenChange={(o) => !o && close()}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-40 bg-black/30" />
        <D.Content
          aria-describedby={undefined}
          data-testid="page-peek"
          className="nx-pop-in fixed inset-x-0 top-[5vh] z-50 mx-auto flex h-[90vh] w-[min(64rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-border bg-bg shadow-pop"
        >
          <div className="flex shrink-0 items-center gap-2 border-b border-border px-4 py-2">
            <D.Title className="min-w-0 flex-1 truncate text-sm font-medium text-fg-muted">
              {page?.title || t('page.untitled')}
            </D.Title>
            <button
              type="button"
              onClick={() => {
                if (page) useUi.getState().open({ kind: 'page', pageId: page.id });
                close();
              }}
              className="flex items-center gap-1.5 rounded-full px-3 py-1 text-sm text-fg-muted hover:bg-bg-hover hover:text-fg"
            >
              <Maximize2 className="size-3.5" />
              {t('peek.openAsPage')}
            </button>
            <D.Close asChild>
              <CloseButton />
            </D.Close>
          </div>
          <div className="mr-1.5 mb-2 min-h-0 flex-1 overflow-y-auto">
            {page && pages && (
              <PageView
                key={page.id}
                page={page}
                children={pages.list.filter((p) => p.parentId === page.id)}
              />
            )}
          </div>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
